import { getIsProdEnv, isDebug } from "@/constants";
import { Context as DiceWorkerContext } from "@/context";
import { getWorkerName } from "@/helpers/getWorkerName";
import { useEffect, useState, type FC, type PropsWithChildren } from "react";

const getWorker = async (nodeEnv: string, env: string) => {
  if (process.env.NODE_ENV === "development") {
    const { getDevWorker } = await import("./getDevWorker");

    return getDevWorker(nodeEnv, env);
  }

  return new Worker(
    `/dice/${getIsProdEnv(env) ? "mobile" : "previewer"}/worker.${process.env.NEXT_PUBLIC_WORKER_HASH}.js`,
    {
      name: getWorkerName(nodeEnv, env),
      type: "module",
    },
  );
};

interface ContextProviderProps extends PropsWithChildren {
  isShared3dDiceSettingEnabled: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- logger type needs to be properly decoupled from feywild
  logger: any;
  errorCallback: () => void;
  nodeEnv: string;
  env: string;
}

export const ContextProvider: FC<ContextProviderProps> = ({
  children,
  isShared3dDiceSettingEnabled,
  logger,
  errorCallback,
  nodeEnv,
  env,
}) => {
  const [worker, setWorker] = useState<Worker>();

  useEffect(() => {
    if (isShared3dDiceSettingEnabled) {
      if (!worker) {
        (async () => {
          const newWorker = await getWorker(nodeEnv, env);

          newWorker.onerror = (error) => {
            // replace logger.error with alert() for mobile debugging
            logger.error("dice worker error", {
              exception: JSON.stringify(error.message),
            });
            errorCallback();
          };

          if (isDebug) {
            newWorker.addEventListener("message", (event) => {
              if (event.data.type === "debug") {
                // replace logger.log with alert() for mobile debugging
                logger.log(event.data.payload);
              }
            });
          }

          setWorker(newWorker);
        })();
      }
    } else {
      worker?.terminate();
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setWorker(undefined);
    }
  }, [
    env,
    errorCallback,
    isShared3dDiceSettingEnabled,
    logger,
    nodeEnv,
    worker,
  ]);

  return (
    <DiceWorkerContext.Provider
      value={{
        worker,
        nodeEnv,
        env,
      }}
    >
      {children}
    </DiceWorkerContext.Provider>
  );
};
